import type React from 'react';
import type { DeviceType } from './details/types';

export interface TableColumnDetails {
    key: string;
    visible: boolean;
    width?: number | string;
    label: string;
    sortable?: boolean;
}

export enum IdentificationType {
    vin = 'vin',
}

export enum AssetType {
    bus = 'bus',
    car = 'car',
    trailer = 'trailer',
    truck = 'truck',
    van = 'van',
}

export enum AssetStatus {
    active = 'active',
    archived = 'archived',
}

export interface MasterDataPlugAndCharge {
    pc_id: string;
}

export interface Tachograph {
    brand: string;
    model: string;
    hardware_version?: string;
    software_version?: string;
}

export enum FuelType {
    DIESEL = 'diesel',
    GAS = 'gas',
    ELECTRIC = 'electric',
    HYDROGEN = 'hydrogen',
    CNG = 'cng',
}

export interface Masterdata {
    adr: string | null | undefined;
    air_freight_suitability: string | null | undefined;
    comment: string | null | undefined;
    customs_bond: string | null | undefined;
    cost_center: string | null | undefined;
    fuel_type: FuelType | null | undefined;
    number_of_axes: number | null | undefined;
    ownership: MasterDataOwnership | null | undefined;
    plug_and_charge_ids: MasterDataPlugAndCharge[] | null | undefined;
    tires: string | null | undefined;
    vehicle_model: string | null | undefined;
    engine_type: string | null | undefined;
    tachograph?: Tachograph | undefined;
}

export enum OwnershipType {
    FINANCING = 'financing',
    LEASING = 'leasing',
    OWNERSHIP = 'ownership',
    RENTAL = 'rental',
}

export interface MasterDataOwnership {
    contract_duration_in_months: number | null | undefined;
    contract_rate: number | null | undefined;
    ownership_end_at: string | null | undefined;
    ownership_start_at: string | null | undefined;
    reference: string | null | undefined;
    registered_first_at: string | null | undefined;
    type: OwnershipType | null | undefined;
}

export interface Asset {
    id: string;
    account_id: string;
    name: string;
    identification: string | null;
    identification_type: IdentificationType | null;
    type: AssetType;
    status: AssetStatus;
    brand: string | null;
    license_plate: string | null;
    license_plate_country_code: string | null;
    tags: string[];
    masterdata: Masterdata | null;
}

export interface AssetWithEmbed {
    id: string;
    account_id: string;
    name: string;
    identification: string | null;
    identification_type: IdentificationType | null;
    type: AssetType;
    status: AssetStatus;
    brand: string | null;
    license_plate: string | null;
    license_plate_country_code: string | null;
    _embedded?: {
        tags?: {
            items: { id: string }[];
        };
        master_data?: Masterdata | null;
    };
}

export interface Device {
    id: string;
    identification: string;
    type: DeviceType;

    [key: string]: string;
}

export interface AssociatedDevice {
    device: Device;
    associationId: string;
}

export interface AssetListProperties {
    assets: Asset[];
    devices: { [key: string]: AssociatedDevice[] };
    columns: TableColumnDetails[];
    tableResetKey: number;
    searchString?: string;
    onCreateAsset: () => void;
    tableViewType: string;
    selectedAsset: Asset | null;
    handleActiveRowChange: (assetId: string | undefined) => void;
    tags: Tag[];
    handleSortChange: (column: string) => void;
    sortBy: string | null;
    sortDir: string | null;
    gridLines?: boolean;
    compactRows?: boolean;
    selectedAssetIds: string[];
    handleSelectionChange: (assetIds: string[]) => void;
    handleToggleAll: (shouldSelect: boolean) => void;
    handleColumnResize: (columnKey: string, width?: number | string) => void;
    handleColumnOrderChange: (columnOrder: string[]) => void;
    handleColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
    showAddToGroupDialog: (show: boolean) => void;
    showRemoveFromGroupDialog: (show: boolean) => void;
    showBulkArchiveDialog: (show: boolean) => void;
}

export interface FormInputFieldProperties {
    translationId: string;
    messageValues?: { [key: string]: string };
    disabled: boolean;
    currentValue: string;
    hasError?: boolean;
    hasFeedback?: boolean;
    feedbackMsg?: React.ReactNode;
    onValueChange?: (e: string | null) => void;
    dataAttribute?: string;
    withCopyButton?: boolean;
    hasLengthValidation?: boolean;
    maxLength?: number;
    type?: FormInputFieldType;
    icon?: React.ReactNode;
}

export enum FormInputFieldType {
    TEXT = 'text',
    TEXTAREA = 'textarea',
}

export interface Tag {
    id: string;
    type: string;
    name: string;
    account_id: string;
}

export type AssetTagListIcon = 'tag' | 'group' | 'device';

export interface AssetTagListItem {
    id: string;
    label: string;
}

export interface AssetTagListProps {
    items: AssetTagListItem[];
    icon?: AssetTagListIcon;
}

export interface AssetTagsProps {
    assetTags: string[];
    tags: Tag[];
    icon?: Exclude<AssetTagListIcon, 'device'>;
}

export interface AssetDevicesProps {
    assetDevices: AssociatedDevice[];
    icon?: AssetTagListIcon;
}

export interface DeviceTypeProps {
    deviceType: DeviceType;
}
